/* eslint-disable local-rules/no-race-conditions */
/* eslint-disable react-hooks/set-state-in-effect -- Share-modal effects synchronize derived local selection state. */
/**
 * ViralShareButton Component (Refactored)
 *
 * Tier-adaptive share button with viral text generation and IMAGE share cards.
 * Features:
 * - Pre-filled share templates based on drama score and character voices
 * - IMAGE generation with react-native-view-shot (1080x1920px Instagram Stories)
 * - Toggle between TEXT and IMAGE share modes
 * - Screenshot optimization (9:16 ratio for Instagram Stories)
 * - Analytics tracking for share initiated/completed events
 * - Character voice integration (Narrator vs Drama Expert)
 * - Tier-adaptive styling with TierButton
 * - Haptic feedback on share
 * - Fallback to text sharing if image generation fails
 *
 * ## Share Flow
 * ```
 * 1. User clicks share button
 * 2. Modal opens with templates
 * 3. User selects template
 * 4. [IMAGE mode] ShareCardRenderer captures card as JPEG
 * 5. expo-sharing shares image (with text fallback on error)
 * 6. Analytics tracking
 * ```
 *
 * Usage:
 * ```tsx
 * import { ViralShareButton } from './components/ViralShareButton';
 *
 * <ViralShareButton
 *   analysis={analysisData}
 *   context="dating"
 *   analysisId={analysisData.id ?? analysisData.result?.id ?? undefined}
 *   variant="primary"
 *   shareMode="image" // 'text' | 'image'
 * />
 * ```
 *
 * Created: 2025-01-11 (Phase 1.1 - Tier-Adaptive Visual Identity)
 * Enhanced: 2025-01-11 (Task 2.2: Viral Share Formats - Image Generation)
 * Refactored: 2025-11-21 (Line Count Refactor: 538 → 183 lines)
 */
import React, { useEffect, useState } from "react";
import { View, Text, Modal, Pressable, ScrollView } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useTranslation } from "@dramadetector/shared";
import type {
	DramaAnalysisResponse,
	MessageContext,
} from "@dramadetector/shared";
import type { ShareFormat } from "@dramadetector/shared/og";
import {
	useSDUIConfig,
	toUISpecTier,
	type SDUIShareModalConfig,
} from "@dramadetector/ui/json-render";
import { ShareCardPreview } from "@dramadetector/ui/share";
import { useEntitlements } from "../hooks/useEntitlements";
import { GlassButton } from "./glass";
import { ShareCardRenderer } from "./share/ShareCardRenderer";
import { useViralShare } from "../hooks/useViralShare";
import {
	buildJourneyShareUrl,
	type JourneyShareParams,
} from "../features/share";
import {
	useSharingEnabledFlag,
	useOgImageGenerationFlag,
} from "../hooks/useFeatureFlags";
import { BRAND_COLORS } from "../lib/design-tokens";

interface ViralShareButtonProps {
	/** Analysis result to share */
	analysis: DramaAnalysisResponse;

	/** Conversation context (family, work, friends, dating) */
	context?: MessageContext;

	/** Share mode: 'text' (legacy) or 'image' (new card generation)
	 * @default 'image'
	 */
	shareMode?: "text" | "image";

	/** Session count (for episode title generation in image mode)
	 * @default 1
	 */
	sessionCount?: number;

	/** Analysis ID — required for auto-creation of shareable link */
	analysisId: string;

	/** Button variant (default: 'secondary') */
	variant?: "primary" | "secondary" | "ghost";

	/** Button size (default: 'md') */
	size?: "sm" | "md" | "lg";

	/** Full width button (default: true) */
	fullWidth?: boolean;

	/** Test ID for E2E testing */
	testID?: string;

	/** Journey share data — when provided with sessionComplete, includes OG image URL */
	journeyData?: JourneyShareParams;

	/** Whether the rewrite session is complete (enables journey share card) */
	sessionComplete?: boolean;
}

export function ViralShareButton({
	analysis,
	context,
	shareMode = "image",
	sessionCount = 1,
	analysisId,
	variant = "secondary",
	size = "md",
	fullWidth = true,
	testID = "viral-share-button",
	journeyData,
	sessionComplete,
}: ViralShareButtonProps) {
	const { t } = useTranslation("features");
	const { t: tCommon } = useTranslation("common");

	// Kill switches (Wave 1 feature flags)
	const sharingEnabled = useSharingEnabledFlag();
	const ogImageEnabled = useOgImageGenerationFlag();

	// Build journey OG image URL when rewrite session is complete with positive improvement
	const journeyOgImageUrl =
		ogImageEnabled && sessionComplete && journeyData && journeyData.delta > 0
			? buildJourneyShareUrl(journeyData)
			: undefined;

	// SDUI: referral-share CTA copy is server-configurable via sdui_surface_configs.
	// When shareCtaCopy is non-null, it overrides the i18n default button text.
	const { tier: entitlementTier } = useEntitlements();
	const { config: shareConfig } = useSDUIConfig(
		"referral-share",
		toUISpecTier(entitlementTier),
	);
	const { config: shareModalConfig } = useSDUIConfig(
		"share-modal-config",
		toUISpecTier(entitlementTier),
	);

	const fallbackFormatOptions: SDUIShareModalConfig["availableFormats"] = [
		{
			id: "portrait",
			label: t("share.format.fallback.portrait.label", { defaultValue: "Your Episode" }),
			sublabel: t("share.format.fallback.portrait.sublabel", { defaultValue: "Feed · 4:5" }),
			hint: t("share.format.fallback.portrait.hint", { defaultValue: "Best for Instagram Feed, Facebook" }),
			pw: 30,
			ph: 37.5,
		},
		{
			id: "square",
			label: t("share.format.fallback.square.label", { defaultValue: "Your Score" }),
			sublabel: t("share.format.fallback.square.sublabel", { defaultValue: "Grid · 1:1" }),
			hint: t("share.format.fallback.square.hint", { defaultValue: "Good for profile grids, multi-platform" }),
			pw: 32,
			ph: 32,
		},
		{
			id: "reel",
			label: t("share.format.fallback.reel.label", { defaultValue: "Reality Moment" }),
			sublabel: t("share.format.fallback.reel.sublabel", { defaultValue: "Reel · 9:16" }),
			hint: t("share.format.fallback.reel.hint", { defaultValue: "Best for Reels, TikTok, Stories" }),
			pw: 24,
			ph: 42.5,
		},
		{
			id: "horizontal",
			label: t("share.format.fallback.horizontal.label", { defaultValue: "Quick Take" }),
			sublabel: t("share.format.fallback.horizontal.sublabel", { defaultValue: "Link · 16:9" }),
			hint: t("share.format.fallback.horizontal.hint", { defaultValue: "Best for messaging & link previews" }),
			pw: 40,
			ph: 22.5,
		},
	];
	const formatOptions =
		shareModalConfig.availableFormats.length > 0
			? shareModalConfig.availableFormats
			: fallbackFormatOptions;
	const defaultFormat =
		formatOptions.find((format) => format.id === shareModalConfig.defaultFormat)
			?.id ??
		formatOptions[0]?.id ??
		"reel";

	const SHARE_FORMAT_STORAGE_KEY = "@dramadetector/share-modal:last-format";

	// Format selection state for multi-format share cards
	const [selectedFormat, setSelectedFormat] =
		useState<ShareFormat>(defaultFormat);
	const [persistedFormat, setPersistedFormat] = useState<ShareFormat | null>(
		null,
	);
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
		null,
	);
	const [showStyleOptions, setShowStyleOptions] = useState(false);
	const [showFormatOptions, setShowFormatOptions] = useState(false);
	const [showPublicResultConfirmation, setShowPublicResultConfirmation] = useState(false);
	const [hasAcknowledgedPublicResultDisclosure, setHasAcknowledgedPublicResultDisclosure] =
		useState(false);

	// Use custom hook for share logic
	const viralShareResult = useViralShare({
		analysis,
		context,
		shareMode,
		sessionCount,
		analysisId,
		journeyOgImageUrl,
		selectedFormat,
	});

	const {
		showModal,
		cardData,
		onRendered,
		isSharing,
		templates,
		shareSubjects = [],
		selectedShareSubjectId = null,
		generatedAssetState = "idle",
		cardRendererRef,
		getCardDataForTemplate,
		shortUrl,
		isCreatingLink,
		copiedLink,
		handleOpenShareModal,
		handleCloseShareModal,
		handleShare,
		handlePublishResultLink = () => undefined,
		handleRevokeResultLink = () => undefined,
		handleCopyLink,
		handleSelectShareSubject = () => undefined,
		handleDownloadImage = () => undefined,
		resetGeneratedAsset = () => undefined,
	} = viralShareResult as typeof viralShareResult & {
		shareSubjects?: Array<{
			id: string;
			title: string;
			subtitle?: string;
		}>;
		selectedShareSubjectId?: string | null;
		generatedAssetState?: "idle" | "generating" | "ready" | "failed";
		handleSelectShareSubject?: (id: string) => void;
		handleDownloadImage?: (template?: typeof selectedTemplate) => void | Promise<void>;
		resetGeneratedAsset?: () => void;
	};

	const preferredVoice =
		shareModalConfig.templateOrdering === "expert-first" ||
		shareModalConfig.shareModalTone === "editorial"
			? "expert"
			: "narrator";
	const orderedTemplates = [...templates].sort((a, b) => {
		const rankA = a.voice === preferredVoice ? 0 : 1;
		const rankB = b.voice === preferredVoice ? 0 : 1;
		return rankA - rankB;
	});
	const resolvedInitialFormat =
		persistedFormat &&
		formatOptions.some((format) => format.id === persistedFormat)
			? persistedFormat
			: defaultFormat;
	const defaultTemplateId = orderedTemplates[0]?.id ?? null;
	const selectedTemplate =
		orderedTemplates.find((template) => template.id === selectedTemplateId) ??
		orderedTemplates[0] ??
		null;
	const previewCardData = selectedTemplate
		? getCardDataForTemplate(selectedTemplate)
		: null;
	const selectedFormatOption =
		formatOptions.find((format) => format.id === selectedFormat) ??
		formatOptions[0] ??
		null;
	const formatHint = shareModalConfig.showFormatHint
		? (selectedFormatOption?.hint ?? null)
		: null;
	const templateSectionCopy =
		shareModalConfig.shareModalTone === "editorial"
			? t("share.modal.template_editorial", {
					defaultValue: "Choose the angle for your panel-worthy reveal.",
				})
			: shareModalConfig.shareModalTone === "clinical"
				? t("share.modal.template_clinical", {
						defaultValue: "Choose the clearest way to frame the pattern.",
					})
				: t("share.modal.template_playful", {
						defaultValue: "Choose the vibe for your reality-show moment.",
					});
	const previewSupportCopy = t("share.modal.preview_support", {
		defaultValue: "Preview the exact image asset before you share it.",
	});
	const formatHelperCopy = t("share.modal.format_helper", {
		defaultValue:
			"Choose where this image will be posted. This changes the exported image size and crop.",
	});
	const selectionSupportCopy = t("share.modal.selection_helper", {
		defaultValue:
			"Image size changes the export. Post style changes the voice and caption.",
	});
	const primaryShareLabel =
		shareMode === "image"
			? generatedAssetState === "ready"
				? t("share.modal.share_image_cta", {
						defaultValue: "Share image",
					})
				: t("share.modal.primary_image_cta", {
						defaultValue: "Generate image",
					})
			: t("share.modal.primary_text_cta", {
					defaultValue: "Share post text",
				});
	const downloadImageLabel = t("share.modal.download_image_cta", {
		defaultValue: "Download image",
	});
	const secondaryLinkTitle = t("share.modal.secondary_link_title", {
		defaultValue: "Copy link instead",
	});
	const secondaryLinkDescription = t("share.modal.secondary_link_description", {
		defaultValue:
			"Use a link when you want the interactive page instead of an image asset.",
	});
	const publishResultLinkTitle = t("share.modal.publish_result_link_title", {
		defaultValue: "Publish public score and summary",
	});
	const publishResultLinkDisclosure = t("share.modal.publish_result_link_disclosure", {
		defaultValue: "Only your drama score and summary will be public.",
	});
	const revokeResultLinkTitle = t("share.modal.revoke_result_link_title", {
		defaultValue: "Revoke public link",
	});
	const revokeResultLinkDisclosure = t("share.modal.revoke_result_link_disclosure", {
		defaultValue: "This immediately makes this public link unavailable.",
	});
	const browseStylesLabel = t("share.modal.browse_styles", {
		defaultValue: "Browse",
	});
	const changeSizeLabel = t("share.modal.change_size", {
		defaultValue: "Change",
	});
	const subjectSectionTitle = t("share.modal.subject_title", {
		defaultValue: "Choose insight",
	});
	const subjectSectionCopy = t("share.modal.subject_helper", {
		defaultValue:
			"Pick the exact finding or summary that becomes the image content.",
	});

	useEffect(() => {
		let active = true;
		void AsyncStorage.getItem(SHARE_FORMAT_STORAGE_KEY)
			.then((value) => {
				if (!active || !value) {
					return;
				}
				if (formatOptions.some((format) => format.id === value)) {
					setPersistedFormat(value as ShareFormat);
				}
			})
			.catch(() => undefined);
		return () => {
			active = false;
		};
	}, [formatOptions]);

	useEffect(() => {
		void AsyncStorage.setItem(SHARE_FORMAT_STORAGE_KEY, selectedFormat).catch(
			() => undefined,
		);
	}, [selectedFormat]);

	useEffect(() => {
		if (showModal) {
			setSelectedFormat(resolvedInitialFormat);
			setShowFormatOptions(false);
			setShowStyleOptions(false);
		}
	}, [resolvedInitialFormat, showModal]);

	useEffect(() => {
		if (showModal) {
			setSelectedTemplateId(defaultTemplateId);
		}
	}, [defaultTemplateId, showModal]);

	useEffect(() => {
		if (!formatOptions.some((format) => format.id === selectedFormat)) {
			setSelectedFormat(resolvedInitialFormat);
		}
	}, [formatOptions, resolvedInitialFormat, selectedFormat]);

	useEffect(() => {
		if (
			selectedTemplateId &&
			orderedTemplates.some((template) => template.id === selectedTemplateId)
		) {
			return;
		}
		setSelectedTemplateId(orderedTemplates[0]?.id ?? null);
	}, [orderedTemplates, selectedTemplateId]);

	// Use short URL as display URL
	const displayUrl = shortUrl;
	const publicResultScore = analysis.result?.score ?? analysis.score ?? null;
	const publicResultSummary = analysis.result?.summary ?? analysis.summary ?? null;
	const publicResultPreviewSummary =
		typeof publicResultSummary === "string" && publicResultSummary.trim().length > 0
			? publicResultSummary
			: t("share.public_result.summary_unavailable", {
					defaultValue: "Summary unavailable",
				});
	const beginPublicResultConfirmation = () => {
		setHasAcknowledgedPublicResultDisclosure(false);
		setShowPublicResultConfirmation(true);
	};
	const cancelPublicResultConfirmation = () => {
		setHasAcknowledgedPublicResultDisclosure(false);
		setShowPublicResultConfirmation(false);
	};
	const confirmPublicResultPublication = () => {
		if (!hasAcknowledgedPublicResultDisclosure) return;
		cancelPublicResultConfirmation();
		void handlePublishResultLink();
	};

	useEffect(() => {
		if (!showModal) {
			setHasAcknowledgedPublicResultDisclosure(false);
			setShowPublicResultConfirmation(false);
		}
	}, [showModal]);

	const buttonVariant = variant === "primary" ? "primary" : "accent";
	const buttonSize = size === "sm" ? "compact" : "normal";

	// Kill switch: hide the entire share button when sharing is disabled
	if (!sharingEnabled) {
		return null;
	}

	return (
		<>
			{/* Share Button - Glass Design */}
			<GlassButton
				variant={buttonVariant}
				size={buttonSize}
				style={fullWidth ? { width: "100%" } : undefined}
				onPress={handleOpenShareModal}
				disabled={isSharing}
				testID={testID}
			>
				{shareConfig.shareCtaCopy ??
					t("share.button", { defaultValue: "Share Your Results" })}
			</GlassButton>

			{/* Share Template Modal */}
			<Modal
				visible={showModal}
				transparent={true}
				animationType="slide"
				onRequestClose={handleCloseShareModal}
			>
				<View className="flex-1 bg-black/50 justify-end">
					<View className="bg-white rounded-t-3xl p-6 max-h-[80%]">
						{/* Header */}
						<View className="flex-row items-center justify-between mb-4">
							<Text className="text-2xl font-bold text-gray-900">
								{t("share.modal.title")}
							</Text>
							<Pressable
								onPress={handleCloseShareModal}
								className="w-11 h-11 items-center justify-center"
								accessibilityLabel={tCommon("close")}
								accessibilityRole="button"
							>
								<Text className="text-gray-400 text-2xl">×</Text>
							</Pressable>
						</View>

						{/* Subtitle */}
						<Text className="text-base text-gray-600 mb-6">
							{t("share.modal.subtitle")}
						</Text>

						<ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
							{/* Live Preview */}
							{shareModalConfig.showPreview && previewCardData && (
								<View className="mb-5">
									<Text className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wide">
										{t("share.modal.preview_title", {
											defaultValue: "Preview",
										})}
									</Text>
									<Text className="text-sm text-gray-500 mb-3">
										{previewSupportCopy}
									</Text>
									<View className="rounded-3xl border border-gray-200 bg-gray-50 px-4 py-5 items-center">
										<ShareCardPreview
											cardData={previewCardData}
											format={selectedFormat}
											previewWidth={240}
											testID={`${testID}-preview`}
										/>
										<Text className="text-xs text-gray-500 mt-3 text-center">
											{selectionSupportCopy}
										</Text>
									</View>
								</View>
							)}

							{/* Share Subject Picker */}
							<View className="mb-5">
								<Text className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wide">
									{subjectSectionTitle}
								</Text>
								<Text className="text-sm text-gray-500 mb-3">
									{subjectSectionCopy}
								</Text>
								{shareSubjects.map((subject) => {
									const isSelected = selectedShareSubjectId === subject.id;
									return (
										<Pressable
											key={subject.id}
											onPress={() => handleSelectShareSubject(subject.id)}
											className={`mb-3 p-4 rounded-xl border ${
												isSelected
													? "bg-teal-50"
													: "border-gray-200 bg-gray-50 active:bg-gray-100"
											}`}
											style={isSelected ? { borderColor: BRAND_COLORS.primaryLight } : undefined}
											accessibilityRole="button"
											accessibilityLabel={`${subject.title}${subject.subtitle ? `, ${subject.subtitle}` : ""}`}
											accessibilityState={{ selected: isSelected }}
											testID={`share-subject-${subject.id}`}
										>
											<View className="flex-row items-center justify-between">
												<View className="flex-1 pr-3">
													<Text className="text-lg font-semibold text-gray-900">
														{subject.title}
													</Text>
													{subject.subtitle ? (
														<Text className="text-sm text-gray-500 mt-1">
															{subject.subtitle}
														</Text>
													) : null}
												</View>
												<View
													className={`px-3 py-1 rounded-full ${
														isSelected ? "bg-teal-100" : "bg-gray-200"
													}`}
												>
													<Text
														className={`text-xs font-semibold ${
															isSelected ? "text-gray-700" : "text-gray-600"
														}`}
													>
														{isSelected
															? t("share.modal.subject_selected", {
																	defaultValue: "Using",
																})
															: t("share.modal.select_subject", {
																	defaultValue: "Choose",
																})}
													</Text>
												</View>
											</View>
										</Pressable>
									);
								})}
							</View>

							{/* Post Style */}
							<View className="mb-5">
								<Text className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wide">
									{t("share.modal.post_style_title", {
										defaultValue: "Post Style",
									})}
								</Text>
								<Text className="text-sm text-gray-500 mb-3">
									{templateSectionCopy}
								</Text>
								<Pressable
									onPress={() => setShowStyleOptions((current) => !current)}
									className="mb-3 p-4 rounded-xl border border-gray-200 bg-gray-50"
									testID="share-style-toggle"
									accessibilityRole="button"
									accessibilityLabel={t("share.modal.post_style_toggle", { defaultValue: "Browse post styles" })}
									accessibilityState={{ expanded: showStyleOptions }}
								>
									<View className="flex-row items-center justify-between">
										<View className="flex-1 pr-3">
											<Text className="text-lg font-semibold text-gray-900">
												{selectedTemplate?.title}
											</Text>
											<Text className="text-sm text-gray-500 mt-1">
												{selectedTemplate?.description}
											</Text>
										</View>
										<View className="px-3 py-1 bg-gray-200 rounded-full">
											<Text className="text-xs font-semibold text-gray-700">
												{browseStylesLabel}
											</Text>
										</View>
									</View>
								</Pressable>
								{showStyleOptions &&
									orderedTemplates.map((template) => {
										const isSelected = selectedTemplate?.id === template.id;
										return (
											<Pressable
												key={template.id}
																			onPress={() => {
																				if (selectedTemplate?.id !== template.id) {
																					resetGeneratedAsset();
																				}
																				setSelectedTemplateId(template.id);
																				setShowStyleOptions(false);
																			}}

												className={`mb-4 p-4 rounded-xl border ${
													isSelected
														? "bg-teal-50"
														: "border-gray-200 bg-gray-50 active:bg-gray-100"
												}`}
												style={isSelected ? { borderColor: BRAND_COLORS.primaryLight } : undefined}
												accessibilityRole="button"
												accessibilityLabel={`${t("share.modal.select_template")}: ${template.title}`}
												accessibilityState={{ selected: isSelected }}
												testID={`share-template-${template.id}`}
											>
												<View className="flex-row items-center justify-between mb-2">
													<View className="flex-1">
														<Text className="text-lg font-semibold text-gray-900">
															{template.title}
														</Text>
														<Text className="text-sm text-gray-500 mt-1">
															{template.description}
														</Text>
													</View>
													<View className="flex-row items-center gap-2">
														{isSelected && (
															<Text className="text-xs font-semibold text-gray-700">
																{t("share.modal.selected", {
																	defaultValue: "Selected",
																})}
															</Text>
														)}
														<View
															className={`px-3 py-1 rounded-full ${
																template.voice === "narrator"
																	? "bg-purple-100"
																	: "bg-blue-100"
															}`}
														>
															<Text
																className={`text-xs font-semibold ${
																	template.voice === "narrator"
																		? "text-purple-700"
																		: "text-blue-700"
																}`}
															>
																{template.voice === "narrator"
																	? t("share.voice.narrator")
																	: t("share.voice.expert")}
															</Text>
														</View>
													</View>
												</View>
												<View className="mt-3 p-3 bg-white rounded-lg border border-gray-100">
													<Text className="text-sm text-gray-700 leading-relaxed">
														{template.shareText}
													</Text>
													<View className="flex-row flex-wrap mt-2">
														{template.hashtags.map((hashtag) => (
															<Text
																key={hashtag}
																className="text-xs text-blue-500 mr-2"
															>
																{hashtag}
															</Text>
														))}
													</View>
												</View>
											</Pressable>
										);
									})}
							</View>

							{/* Image Size Picker */}
							<View className="mb-5">
								<Text className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wide">
									{t("share.modal.format_title", {
										defaultValue: "Image Size",
									})}
								</Text>
								<Text className="text-sm text-gray-500 mb-3">
									{formatHelperCopy}
								</Text>
								<Pressable
									onPress={() => setShowFormatOptions((current) => !current)}
									className="mb-3 p-4 rounded-xl border border-gray-200 bg-gray-50"
									testID="share-format-toggle"
									accessibilityRole="button"
									accessibilityLabel={t("share.modal.format_toggle", { defaultValue: "Change image size" })}
									accessibilityState={{ expanded: showFormatOptions }}
								>
									<View className="flex-row items-center justify-between">
										<View className="flex-1 pr-3">
											<Text className="text-lg font-semibold text-gray-900">
												{selectedFormatOption?.label}
											</Text>
											<Text className="text-sm text-gray-500 mt-1">
												{selectedFormatOption?.sublabel}
											</Text>
										</View>
										<View className="px-3 py-1 bg-gray-200 rounded-full">
											<Text className="text-xs font-semibold text-gray-700">
												{changeSizeLabel}
											</Text>
										</View>
									</View>
								</Pressable>
								{showFormatOptions && (
									<>
										<View className="flex-row gap-2">
											{formatOptions.map((f) => (
												<Pressable
													key={f.id}
																			onPress={() => {
																				if (selectedFormat !== f.id) {
																					resetGeneratedAsset();
																				}
																				setSelectedFormat(f.id);
																				setShowFormatOptions(false);
																			}}

													className={`flex-1 p-3 rounded-xl border-2 items-center ${
														selectedFormat === f.id
															? "bg-teal-50"
															: "border-gray-200 bg-gray-50 opacity-80"
													}`}
													style={selectedFormat === f.id ? { borderColor: BRAND_COLORS.primaryLight } : undefined}
													testID={`format-picker-${f.id}`}
													accessibilityRole="radio"
													accessibilityLabel={f.label}
													accessibilityState={{ selected: selectedFormat === f.id }}
												>
													<View
														className={`rounded border mb-2 ${
															selectedFormat === f.id
																? "bg-teal-100"
																: "border-gray-300 bg-gray-100"
														}`}
														style={selectedFormat === f.id ? { borderColor: BRAND_COLORS.primaryLight, width: f.pw, height: f.ph } : { width: f.pw, height: f.ph }}
													/>
													<View className="flex-row items-center gap-1">
														{selectedFormat === f.id && (
															<Text className="text-gray-700 text-xs" style={{ color: BRAND_COLORS.primary }}>✓</Text>
														)}
														<Text
															className={`text-xs font-semibold ${
																selectedFormat === f.id
																	? "text-gray-700"
																	: "text-gray-700"
															}`}
														>
															{f.label}
														</Text>
													</View>
													<Text className="text-[10px] text-gray-400 text-center">
														{f.sublabel}
													</Text>
												</Pressable>
											))}
										</View>
										{formatHint && (
											<Text className="text-xs text-gray-700 mt-2 text-center" style={{ color: BRAND_COLORS.primary }}>
												{formatHint}
											</Text>
										)}
									</>
								)}
							</View>

							<View className="mt-2 mb-4 rounded-2xl border border-gray-200 bg-white p-4">
								<Text className="text-sm font-semibold text-gray-900 mb-1">
									{generatedAssetState === "failed"
										? t("share.modal.asset_failed_title", {
												defaultValue: "Image generation failed",
											})
										: t("share.modal.export_title", {
												defaultValue: "Export share-ready image",
											})}
								</Text>
								<Text className="text-sm text-gray-500 mb-3">
									{generatedAssetState === "failed"
										? t("share.modal.asset_failed_description", {
												defaultValue:
													"Try generating again or copy the link instead.",
											})
										: t("share.modal.export_description", {
												defaultValue:
													generatedAssetState === "ready"
														? "Your image is ready. Share or download the generated asset."
														: "Generate the selected image asset before sharing or downloading it.",
											})}
								</Text>
								<Pressable
									onPress={() =>
										selectedTemplate && void handleShare(selectedTemplate)
									}
									disabled={!selectedTemplate || isSharing}
									className={`items-center justify-center rounded-xl px-4 py-4 ${
										!selectedTemplate || isSharing
											? "bg-gray-200"
											: "bg-transparent"
									}`}
									style={!selectedTemplate || isSharing ? undefined : { backgroundColor: BRAND_COLORS.primary }}
									testID="share-primary-action"
								>
									<Text className="text-base font-semibold text-white">
										{primaryShareLabel}
									</Text>
								</Pressable>
								{generatedAssetState === "ready" && (
									<View className="mt-3 flex-row gap-2">
										<Pressable
											onPress={() =>
												void handleDownloadImage(selectedTemplate ?? undefined)
											}
											className="flex-1 items-center justify-center rounded-xl px-4 py-3 bg-gray-100"
											testID="share-download-action"
										>
											<Text className="text-sm font-semibold text-gray-700">
												{downloadImageLabel}
											</Text>
										</Pressable>
									</View>
								)}
							</View>

							{/* Public Result Link — explicit confirmation, never auto-created. */}
							<Pressable
								onPress={displayUrl ? handleCopyLink : beginPublicResultConfirmation}
								disabled={isCreatingLink}
								className="mb-4 p-4 bg-indigo-50 rounded-xl border border-indigo-200 active:bg-indigo-100 disabled:opacity-50"
								accessibilityRole="button"
								accessibilityLabel={displayUrl ? secondaryLinkTitle : publishResultLinkTitle}
								testID="share-link-option"
							>
								<View className="flex-row items-center justify-between mb-2">
									<View className="flex-1">
										<Text className="text-lg font-semibold text-indigo-900">
											{displayUrl ? secondaryLinkTitle : publishResultLinkTitle}
										</Text>
										<Text className="text-sm text-indigo-600 mt-1">
											{isCreatingLink && !displayUrl
												? t("share.link_option.creating")
												: displayUrl
													? secondaryLinkDescription
													: publishResultLinkDisclosure}
										</Text>
									</View>
									<View className="px-3 py-1 bg-indigo-200 rounded-full">
										<Text className="text-xs font-semibold text-indigo-800">
											{displayUrl && copiedLink
												? t("share.link_option.copied")
												: displayUrl
													? t("share.link_option.label")
													: t("share.link_option.publish", { defaultValue: "Review" })}
										</Text>
									</View>
								</View>
								{displayUrl && (
									<View className="mt-2 p-2 bg-white rounded-lg border border-indigo-100">
										<Text
											className="text-xs text-indigo-700 font-mono"
											numberOfLines={1}
										>
											{displayUrl}
										</Text>
									</View>
								)}
							</Pressable>
							{!displayUrl && showPublicResultConfirmation && (
								<View className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
									<Text className="text-lg font-semibold text-indigo-900">
										{t("share.public_result.review_title", { defaultValue: "Review public result" })}
									</Text>
									<Text className="mt-2 text-sm text-indigo-800">
										{t("share.public_result.preview_fields", {
											defaultValue: "Only this score and summary will be public.",
										})}
									</Text>
									<View className="mt-3 rounded-lg bg-white p-3">
										<Text className="text-xs font-semibold uppercase text-gray-500">
											{t("share.public_result.score_label", { defaultValue: "Score" })}
										</Text>
										<Text className="text-base text-gray-900">{publicResultScore ?? "—"}</Text>
										<Text className="mt-2 text-xs font-semibold uppercase text-gray-500">
											{t("share.public_result.summary_label", { defaultValue: "Summary" })}
										</Text>
										<Text className="text-base text-gray-900">{publicResultPreviewSummary}</Text>
									</View>
									<Pressable
										onPress={() => setHasAcknowledgedPublicResultDisclosure((value) => !value)}
										className="mt-3 min-h-12 flex-row items-start rounded-lg border border-indigo-300 p-3"
										accessibilityRole="checkbox"
										accessibilityState={{ checked: hasAcknowledgedPublicResultDisclosure }}
										accessibilityLabel={t("share.public_result.disclosure", {
											defaultValue:
												"I understand anyone with this link can view this result, it expires with my analysis, and I can revoke it but cannot control external copies.",
										})}
										testID="acknowledge-public-result-disclosure"
									>
										<View
											className={`mr-3 h-6 w-6 shrink-0 items-center justify-center rounded border-2 ${hasAcknowledgedPublicResultDisclosure ? "border-indigo-700 bg-indigo-700" : "border-indigo-500 bg-white"}`}
											testID="public-result-consent-indicator"
										>
											{hasAcknowledgedPublicResultDisclosure && (
												<Text className="text-sm font-bold text-white">✓</Text>
											)}
										</View>
										<Text className="flex-1 text-sm text-indigo-900">
											{t("share.public_result.disclosure", {
												defaultValue:
													"I understand anyone with this link can view this result, it expires with my analysis, and I can revoke it but cannot control external copies.",
											})}
										</Text>
									</Pressable>
									{!hasAcknowledgedPublicResultDisclosure && (
										<Text className="mt-1 text-sm text-indigo-700">
											{t("share.public_result.acknowledge_hint", {
												defaultValue: "Tap the checkbox to agree before publishing.",
											})}
										</Text>
									)}
									<View className="mt-3 flex-row gap-2">
										<Pressable
											onPress={cancelPublicResultConfirmation}
											className="flex-1 items-center rounded-lg border border-gray-300 p-3"
											testID="cancel-publish-public-result"
										>
											<Text className="font-semibold text-gray-700">
												{t("common.cancel", { defaultValue: "Cancel" })}
											</Text>
										</Pressable>
										<Pressable
											onPress={confirmPublicResultPublication}
											disabled={!hasAcknowledgedPublicResultDisclosure || isCreatingLink}
											className="flex-1 items-center rounded-lg bg-indigo-700 p-3 disabled:opacity-50"
											testID="confirm-publish-public-result"
										>
											<Text className="font-semibold text-white">
												{t("share.public_result.confirm", { defaultValue: "Confirm publish" })}
											</Text>
										</Pressable>
									</View>
								</View>
							)}
							{displayUrl && (
								<Pressable
									onPress={() => void handleRevokeResultLink()}
									disabled={isCreatingLink}
									className="mb-4 p-4 rounded-xl border border-red-200 bg-red-50 active:bg-red-100 disabled:opacity-50"
									accessibilityRole="button"
									accessibilityLabel={revokeResultLinkTitle}
									testID="revoke-share-link-option"
								>
									<Text className="text-base font-semibold text-red-800">
										{revokeResultLinkTitle}
									</Text>
									<Text className="mt-1 text-sm text-red-700">
										{revokeResultLinkDisclosure}
									</Text>
								</Pressable>
							)}
						</ScrollView>
					</View>
				</View>
			</Modal>

			{/* Off-Screen Card Renderer (Image Generation) */}
			{cardData && (
				<ShareCardRenderer
					ref={cardRendererRef}
					cardData={cardData}
					format={selectedFormat}
					onRendered={onRendered}
					testID={`${testID}-renderer`}
				/>
			)}
		</>
	);
}
